import React, { useState, useRef, useEffect } from 'react';

export interface PondOption {
  id: string;
  name: string;
  recordCount?: number;
}

interface PondFilterProps {
  ponds: PondOption[];
  selectedPondId: string | null; // null = "All Ponds"
  onChange: (pondId: string | null) => void;
  label?: string;
  className?: string;
}

export const PondFilter: React.FC<PondFilterProps> = ({
  ponds,
  selectedPondId,
  onChange,
  label = 'Filter by Pond',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedPond = ponds.find(p => p.id === selectedPondId);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (pondId: string | null) => {
    onChange(pondId);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-full rounded-lg border border-slate-200/50 bg-white px-4 py-2.5 text-left shadow-sm transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-900">
            {selectedPond ? selectedPond.name : 'All Ponds'}
          </span>
          <svg
            className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full rounded-lg border border-slate-200/50 bg-white shadow-lg">
          <div className="max-h-60 overflow-auto py-1">
            {/* All Ponds option */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={`
                flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors
                ${!selectedPondId ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'}
              `}
            >
              <div
                className={`h-4 w-4 rounded border-2 flex items-center justify-center ${
                  !selectedPondId ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
                }`}
              >
                {!selectedPondId && (
                  <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="flex-1 text-left">All Ponds</span>
            </button>

            {/* Individual pond options */}
            {ponds.map((pond) => {
              const isSelected = selectedPondId === pond.id;

              return (
                <button
                  key={pond.id}
                  type="button"
                  onClick={() => handleSelect(pond.id)}
                  className={`
                    flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors
                    ${isSelected ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'}
                  `}
                >
                  <div
                    className={`h-4 w-4 rounded border-2 flex items-center justify-center ${
                      isSelected ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
                    }`}
                  >
                    {isSelected && (
                      <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="flex-1 text-left">{pond.name}</span>
                  {pond.recordCount !== undefined && (
                    <span className="text-xs text-gray-500">{pond.recordCount}</span>
                  )}
                </button>
              );
            })}
          </div>

          {selectedPond && (
            <div className="border-t border-slate-200/50 px-3 py-2">
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                Clear filter
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
